/**
 * Plan 94 - Device Workspace tab configuration.
 *
 * The device workspace (`/devices/:deviceId/:tab`) replaces the old per-page
 * device selectors. Each tab maps to a per-device tool and is gated by the same
 * permission the equivalent legacy page used.
 *
 * The SELECTED TAB is a UI preference persisted in localStorage so it follows the
 * operator across device switches. The SELECTED DEVICE is never stored — the URL
 * is the single source of truth for device identity (this is the deliberate
 * correction to the Plan 89 cross-page "pinned device" mechanism).
 */
export interface WorkspaceTab {
  key: string;
  label: string;
  /** Permission required to see and open the tab, or null for ungated tabs. */
  perm: string | null;
}

export const WORKSPACE_TABS: WorkspaceTab[] = [
  { key: 'overview', label: 'Overview', perm: null },
  { key: 'analysis', label: 'Analysis', perm: null },
  { key: 'notes', label: 'Notes', perm: 'read_notes' },
  { key: 'debug', label: 'Debug', perm: 'debug' },
  { key: 'photos', label: 'Photos', perm: 'firestore_read' },
  { key: 'settings', label: 'Settings', perm: 'read_devices' },
  { key: 'claims', label: 'App Claims', perm: 'read_devices' },
  // FS-529: on-demand mirror of the in-app chart + stats for the selected
  // device (works regardless of app-side per-user enablement).
  { key: 'appgraphs', label: 'App Graphs', perm: 'read_devices' },
  // Plan 123: what is still unknown about this home's system, and the
  // operator path for asking the homeowner. Reading it is `read_devices`;
  // writing to the homeowner needs `contact_users`, which the tab itself
  // gates block by block.
  { key: 'missing', label: 'Missing', perm: 'read_devices' },
  // FS-749 (Plan 128): the proposed filter-limit methods for this
  // system, shown for comparison only. The permission name is general on
  // purpose so later experimental views can share it.
  { key: 'experimental', label: 'Experimental', perm: 'experimental' },
];

export const DEFAULT_TAB = 'overview';
const TAB_PREF_KEY = 'deviceWorkspaceTab';

export const findTab = (tab: string | undefined): WorkspaceTab | undefined =>
  WORKSPACE_TABS.find((t) => t.key === tab);

export const isValidTab = (tab: string | undefined): boolean => !!findTab(tab);

/** Last tab the operator viewed, or the default. Never returns an invalid key. */
export const readStickyTab = (): string => {
  try {
    const v = localStorage.getItem(TAB_PREF_KEY);
    return isValidTab(v ?? undefined) ? (v as string) : DEFAULT_TAB;
  } catch {
    return DEFAULT_TAB;
  }
};

export const writeStickyTab = (tab: string): void => {
  try {
    if (isValidTab(tab)) localStorage.setItem(TAB_PREF_KEY, tab);
  } catch {
    /* localStorage unavailable (private mode) — sticky pref is best-effort */
  }
};
